const money = (value, currency) =>
    new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: 2
    }).format(value)
const percent = (value) =>
    new Intl.NumberFormat(undefined, {
        style: 'percent',
        maximumFractionDigits: 1
    }).format(value)

const Metric = ({ label, children }) => (
    <div>
        <dt className="text-xs font-medium text-slate-500">{label}</dt>
        <dd className="mt-1 text-sm font-semibold text-slate-900">{children}</dd>
    </div>
)

export default function ManagementConstrainedOptimization({ evaluation, requestedCapital, acceptedResponse }) {
    const result = evaluation.result
    const accepted = acceptedResponse || result
    const currency = result?.run_context?.currency
    return (
        <section
            aria-labelledby="management-constrained-title"
            className="rounded-xl border border-cyan-200 bg-cyan-50/40 p-5"
        >
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-800">
                Modeled decision evidence · same analytical authority
            </p>
            <h2 id="management-constrained-title" className="mt-2 text-base font-semibold text-slate-950">
                Recommended Capital Allocation
            </h2>
            <p className="mt-1 text-sm text-slate-600">
                Management-constrained evaluation using the same Portfolio Capital Optimization authority.
            </p>
            {evaluation.status === 'IDLE' && (
                <p role="status" className="mt-4 text-sm text-slate-600">
                    Enter Management Requested Capital and explicitly evaluate to produce same-authority evidence.
                </p>
            )}
            {evaluation.status === 'RUNNING' && (
                <p role="status" className="mt-4 text-sm text-slate-600">
                    Running the accepted Portfolio Capital Optimization authority…
                </p>
            )}
            {(evaluation.status === 'ERROR' || evaluation.status === 'INCOMPARABLE') && (
                <div
                    role="alert"
                    className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                >
                    Comparable Management-constrained optimization is unavailable. Accepted optimizer evidence and
                    Management Requested Capital remain unchanged.
                    {evaluation.reason && <span className="block mt-1 font-mono text-xs">{evaluation.reason}</span>}
                </div>
            )}
            {evaluation.status === 'READY' && result && (
                <>
                    <p className="mt-3 text-sm font-medium text-slate-800">
                        Take forward the Management-constrained modeled allocation for Investment Planning. This is not
                        committed, released, spent, or Board-authorized capital.
                    </p>
                    <div className="mt-4 rounded-lg border border-cyan-200 bg-white p-4">
                        <h3 className="font-semibold">Capital allocation comparison</h3>
                        <p className="text-xs text-slate-500">
                            Accepted Modeled Position → Management-Constrained Position
                        </p>
                        <dl className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            <Metric label="Accepted available capital">
                                {money(accepted.available_capital, currency)}
                            </Metric>
                            <Metric label="Accepted deployment">
                                {money(accepted.modeled_optimal_deployment, currency)}
                            </Metric>
                            <Metric label="Accepted EAL">
                                {money(accepted.optimized.expected_annual_loss, currency)}
                            </Metric>
                            <Metric label="Accepted P95">{money(accepted.optimized.p95, currency)}</Metric>
                            <Metric label="Accepted EAL reduction">
                                {money(accepted.impact.eal_reduction, currency)}
                            </Metric>
                            <Metric label="Accepted P95 reduction">
                                {money(accepted.impact.p95_reduction, currency)}
                            </Metric>
                            <Metric label="Accepted Net Benefit">{money(accepted.impact.net_benefit, currency)}</Metric>
                            <Metric label="Accepted ROI">{percent(accepted.impact.roi)}</Metric>
                        </dl>
                    </div>
                    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
                        <h3 className="font-semibold">Modeled Opportunity Gap</h3>
                        <p className="mt-1 text-sm text-slate-600">
                            The trade-off is represented by the optimizer-returned deployment, unallocated capital, EAL,
                            P95, Net Benefit, ROI, and scenario allocations. No independent economic-reference formula
                            or composite score is used.
                        </p>
                    </div>
                    <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <Metric label="Management Requested Capital">
                            {money(Number(requestedCapital), currency)}
                        </Metric>
                        <Metric label="Modeled Optimal Deployment">
                            {money(result.modeled_optimal_deployment, currency)}
                        </Metric>
                        <Metric label="Unallocated Capital">{money(result.unallocated_capital, currency)}</Metric>
                        <Metric label="Capital Utilization">{percent(result.capital_utilization_ratio)}</Metric>
                        <Metric label="Baseline EAL">{money(result.baseline.expected_annual_loss, currency)}</Metric>
                        <Metric label="Optimized EAL">{money(result.optimized.expected_annual_loss, currency)}</Metric>
                        <Metric label="EAL Reduction">{money(result.impact.eal_reduction, currency)}</Metric>
                        <Metric label="P95 Reduction">{money(result.impact.p95_reduction, currency)}</Metric>
                        <Metric label="Net Benefit">{money(result.impact.net_benefit, currency)}</Metric>
                        <Metric label="ROI">{percent(result.impact.roi)}</Metric>
                        <Metric label="Scenario Count">{result.run_context.scenario_ids.length}</Metric>
                        <Metric label="Comparison Status">Comparable</Metric>
                        <Metric label="Modeled Outcome EAL">
                            {money(result.optimized.expected_annual_loss, currency)}
                        </Metric>
                        <Metric label="Modeled Outcome P95">{money(result.optimized.p95, currency)}</Metric>
                    </dl>
                    <details className="mt-4 border-t border-cyan-200 pt-3">
                        <summary className="cursor-pointer text-sm font-medium text-slate-700">
                            Backend allocation ({result.allocation.length} rows)
                        </summary>
                        <div className="mt-2 overflow-x-auto">
                            <table className="min-w-full text-left text-xs">
                                <thead>
                                    <tr>
                                        <th className="py-1 pr-4">Scenario</th>
                                        <th className="py-1">Allocated investment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.allocation.map((row, index) => (
                                        <tr key={`${row.scenario_id}-${index}`}>
                                            <td className="py-1 pr-4">{row.scenario_name || row.scenario_id}</td>
                                            <td className="py-1">{money(row.allocated_investment, currency)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </details>
                </>
            )}
        </section>
    )
}
